import React, { useRef, useState } from 'react';
import { Divider, ListItemText, Menu, MenuItem } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useTranslation } from 'react-i18next';
import GenericButton, { GenericButtonTypeEnum } from '../../../../components/GenericButton';
import { MESSAGE_TYPE } from '../../../../common/constants/common.constants';
import { downloadFinancePdfZip, downloadInvoicesCsv, downloadPaymentsCsv, exportBalances } from '../../../../api/invoicing.api';
import { createNotification } from '../../../../utilities/helper/notification.helper';
import { downloadBlob } from '../../../../utilities/helper/download.helper';
import ExportDialog from './ExportDialog';
import { forMonth, inMonth } from './monthText';
import { KINDERGARTEN_SCOPE, OverviewScope, scopeParams } from './scope';

/**
 * Everything the overview can hand over as a file: the bookkeeper's workbook, and the
 * month's invoices, payments, today's debts and PDFs on their own.
 */
const ExportMenu: React.FC<{ kindergartenId: string; kindergartenName: string; month: string; scope?: OverviewScope }> = ({
  kindergartenId,
  kindergartenName,
  month,
  scope = KINDERGARTEN_SCOPE
}) => {
  const { t } = useTranslation();
  // the bookkeeper's workbook is the kindergarten's; a location or a group gets the month's files
  const params = scopeParams(scope);
  const whole = scope.kind === 'kindergarten';
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // GenericButton hands its click no event, so the menu anchors on a wrapper
  const wrapper = useRef<HTMLSpanElement>(null);
  const [dialog, setDialog] = useState(false);
  const year = Number(month.slice(0, 4));
  const monthNumber = Number(month.slice(5, 7));

  const get = async (load: () => Promise<{ blob: Blob; fileName: string }>) => {
    setAnchor(null);
    try {
      const file = await load();
      downloadBlob(file.blob, file.fileName);
    } catch {
      createNotification(MESSAGE_TYPE.ERROR, t('finance.export.failed'));
    }
  };

  return (
    <>
      <span ref={wrapper}>
        <GenericButton
          buttonType={GenericButtonTypeEnum.Secondary}
          variant="outlined"
          startIcon={<FileDownloadOutlinedIcon />}
          endIcon={<ArrowDropDownIcon />}
          label={t('finance.export.button')}
          aria-haspopup="menu"
          onClick={() => setAnchor(wrapper.current)}
        />
      </span>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} slotProps={{ paper: { sx: { width: 360 } } }}>
        {whole && [
          <MenuItem
            key="bookkeeper"
            onClick={() => {
              setAnchor(null);
              setDialog(true);
            }}
          >
            <ListItemText
              primary={t('finance.export.bookkeeper')}
              secondary={t('finance.export.bookkeeperHint')}
              slotProps={{ secondary: { sx: { whiteSpace: 'normal' } } }}
            />
          </MenuItem>,
          <Divider key="divider" />
        ]}
        <MenuItem onClick={() => get(() => downloadInvoicesCsv(kindergartenId, year, monthNumber, params))}>
          {t('finance.export.invoicesCsv', { month: forMonth(t, month) })}
        </MenuItem>
        <MenuItem onClick={() => get(() => downloadPaymentsCsv(kindergartenId, year, monthNumber, params))}>
          {t('finance.export.paymentsCsv', { month: inMonth(t, month) })}
        </MenuItem>
        <MenuItem
          onClick={() =>
            get(async () => ({
              blob: await exportBalances(kindergartenId, {
                view: 'child',
                bucket: 'all',
                ...(scope.kind === 'group' ? { groupIds: [scope.groupId] } : params)
              }),
              fileName: 'dugovanja.csv'
            }))
          }
        >
          {t('finance.export.debtsCsv')}
        </MenuItem>
        <MenuItem onClick={() => get(() => downloadFinancePdfZip(kindergartenId, month, month, params))}>
          {t('finance.export.pdfZip', { month: forMonth(t, month) })}
        </MenuItem>
      </Menu>
      {dialog && (
        <ExportDialog kindergartenId={kindergartenId} kindergartenName={kindergartenName} month={month} onClose={() => setDialog(false)} />
      )}
    </>
  );
};

export default ExportMenu;
